import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Alert,
  AlertTitle,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ModernHeader from '../common/ModernHeader';

const Documentation: React.FC = () => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const [expandedSection, setExpandedSection] = useState<string | false>('introduction');

  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const isFrench = i18n.language === 'fr';

  const CodeBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
      component="pre"
      sx={{
        bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
        color: theme.palette.mode === 'dark' ? 'grey.100' : 'text.primary',
        p: 2,
        borderRadius: 1,
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        border: '1px solid',
        borderColor: 'divider',
        my: 2,
      }}
    >
      {children}
    </Box>
  );

  // Composants de style uniformisés
  const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2, fontWeight: 600, fontSize: '1.25rem' }}>
      {children}
    </Typography>
  );

  const SubSectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2.5, mb: 1.5, fontWeight: 600, fontSize: '1.1rem' }}>
      {children}
    </Typography>
  );

  const BodyText: React.FC<{ children: React.ReactNode; paragraph?: boolean }> = ({ children, paragraph = true }) => (
    <Typography variant="body1" paragraph={paragraph} sx={{ fontSize: '1rem', lineHeight: 1.6 }}>
      {children}
    </Typography>
  );

  const SolutionList: React.FC<{ items: Array<{ fr: string; en: string }> }> = ({ items }) => (
    <List sx={{ mt: 1, mb: 2 }}>
      {items.map((item, index) => (
        <ListItem key={index} sx={{ pl: 0 }}>
          <InfoIcon color="info" sx={{ mr: 1.5, flexShrink: 0 }} />
          <ListItemText 
            primary={isFrench ? item.fr : item.en}
            primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
          />
        </ListItem>
      ))}
    </List>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ModernHeader />
      <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {isFrench ? 'Guide d\'Utilisation - Roland.ia' : 'User Guide - Roland.ia'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {isFrench
              ? 'Apprenez à utiliser Roland.ia efficacement pour sélectionner et analyser vos retombées de presse'
              : 'Learn how to use Roland.ia effectively to select and analyze your press coverage'}
          </Typography>
        </Box>

        {/* Table of Contents */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            {isFrench ? '📋 Table des matières' : '📋 Table of Contents'}
          </Typography>
          <List dense>
            {[
              { id: 'introduction', label: isFrench ? 'Introduction' : 'Introduction' },
              { id: 'getting-started', label: isFrench ? 'Premiers pas' : 'Getting Started' },
              { id: 'features', label: isFrench ? 'Fonctionnalités principales' : 'Main Features' },
              { id: 'best-practices', label: isFrench ? 'Bonnes pratiques' : 'Best Practices' },
              { id: 'examples', label: isFrench ? 'Exemples de requêtes' : 'Query Examples' },
              { id: 'troubleshooting', label: isFrench ? 'Résolution de problèmes' : 'Troubleshooting' },
              { id: 'faq', label: 'FAQ' },
              { id: 'advanced-tips', label: isFrench ? 'Conseils avancés' : 'Advanced Tips' },
            ].map((item) => (
              <ListItem
                key={item.id}
                button
                onClick={() => setExpandedSection(item.id)}
                sx={{ borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
              >
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{ sx: { fontSize: '1rem' } }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Introduction */}
        <Accordion expanded={expandedSection === 'introduction'} onChange={handleChange('introduction')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h5">🎯 {isFrench ? 'Introduction' : 'Introduction'}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              {isFrench
                ? 'Bienvenue dans **Roland.ia** ! Cet outil vous permet de sélectionner rapidement et efficacement les retombées de presse les plus pertinentes selon vos critères spécifiques.'
                : 'Welcome to **Roland.ia**! This tool allows you to quickly and efficiently select the most relevant press coverage according to your specific criteria.'}
            </Typography>
            <SectionTitle>{isFrench ? 'Ce que vous pouvez faire' : 'What You Can Do'}</SectionTitle>
            <List>
              {[
                { fr: 'Sélectionner des retombées selon des critères précis (client, ton, notoriété, région, etc.)', en: 'Select coverage according to precise criteria (client, tone, notoriety, region, etc.)' },
                { fr: 'Générer des fichiers CSV téléchargeables avec toutes les métadonnées', en: 'Generate downloadable CSV files with all metadata' },
                { fr: 'Visualiser des données avec des graphiques générés automatiquement', en: 'Visualize data with automatically generated charts' },
                { fr: 'Gérer vos conversations avec historique complet', en: 'Manage your conversations with complete history' },
                { fr: 'Télécharger vos sélections à tout moment', en: 'Download your selections at any time' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <CheckIcon color="success" sx={{ mr: 1.5, flexShrink: 0 }} />
                  <ListItemText 
                    primary={isFrench ? item.fr : item.en}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Getting Started */}
        <Accordion expanded={expandedSection === 'getting-started'} onChange={handleChange('getting-started')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h5">🚀 {isFrench ? 'Premiers pas' : 'Getting Started'}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SectionTitle>{isFrench ? 'Connexion' : 'Login'}</SectionTitle>
            <List>
              {[
                { fr: 'Accédez à l\'application via votre navigateur', en: 'Access the application via your browser' },
                { fr: 'Connectez-vous avec vos identifiants', en: 'Log in with your credentials' },
                { fr: 'Vous arrivez sur le tableau de bord avec vos conversations', en: 'You arrive on the dashboard with your conversations' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`${index + 1}. ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SectionTitle>{isFrench ? 'Créer votre première sélection' : 'Create Your First Selection'}</SectionTitle>
            <List>
              {[
                { fr: 'Cliquez sur "Nouvelle conversation" ou utilisez le bouton "+"', en: 'Click on "New Conversation" or use the "+" button' },
                { fr: 'Tapez votre requête dans la zone de texte en bas', en: 'Type your query in the text box at the bottom' },
                { fr: 'Appuyez sur Entrée ou cliquez sur "Envoyer"', en: 'Press Enter or click "Send"' },
                { fr: 'Roland.ia traite votre demande et génère un fichier CSV téléchargeable', en: 'Roland.ia processes your request and generates a downloadable CSV file' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`${index + 1}. ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Main Features */}
        <Accordion expanded={expandedSection === 'features'} onChange={handleChange('features')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h5">💡 {isFrench ? 'Fonctionnalités principales' : 'Main Features'}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SectionTitle>1. {isFrench ? 'Sélection de retombées' : 'Coverage Selection'}</SectionTitle>
            <BodyText>
              {isFrench
                ? 'Roland.ia peut sélectionner des retombées selon plusieurs critères :'
                : 'Roland.ia can select coverage according to several criteria:'}
            </BodyText>

            <SubSectionTitle>{isFrench ? 'Critères disponibles' : 'Available Criteria'}</SubSectionTitle>
            <List dense>
              {[
                { fr: 'Client : Nom du client ou organisation', en: 'Client: Client name or organization' },
                { fr: 'Nombre de retombées : Quantité souhaitée (ex: "25 retombées")', en: 'Number of coverage: Desired quantity (e.g., "25 coverage")' },
                { fr: 'Ton : Très positif, Positif, Neutre, Négatif, Très négatif', en: 'Tone: Very positive, Positive, Neutral, Negative, Very negative' },
                { fr: 'Perspective : Primaire, Secondaire, Tertiaire', en: 'Perspective: Primary, Secondary, Tertiary' },
                { fr: 'Notoriété : Très connu, Moyennement connu, Peu connu', en: 'Notoriety: Very well-known, Moderately known, Little known' },
                { fr: 'Région : Canada, Le Québec, Québec, Montréal, Toronto, Vancouver, Mauricie, Outaouais, Estrie, Sag-Lac-Saint-Jean, Bas-Saint-Laurent, Laurentides, Centre du Québec, Maritimes', en: 'Region: Canada, Le Québec, Quebec, Montreal, Toronto, Vancouver, Mauricie, Outaouais, Estrie, Sag-Lac-Saint-Jean, Bas-Saint-Laurent, Laurentides, Centre du Québec, Maritimes' },
                { fr: 'Langue : Français, Anglais', en: 'Language: French, English' },
                { fr: 'Type de média : Presse écrite, Radio, Télévision, Web', en: 'Media Type: Print, Radio, Television, Web' },
                { fr: 'Portée : National, Régional', en: 'Reach: National, Regional' },
                { fr: 'Date de publication : Vous pouvez filtrer par période spécifique (ex: "retombées de janvier 2025", "retombées entre janvier et mars 2025", "retombées des 6 derniers mois")', en: 'Publication Date: You can filter by specific period (e.g., "coverage from January 2025", "coverage between January and March 2025", "coverage from the last 6 months")' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={isFrench ? item.fr : item.en}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SubSectionTitle>{isFrench ? 'Ordre de priorité des critères' : 'Priority Order of Criteria'}</SubSectionTitle>
            <BodyText>
              {isFrench
                ? 'Roland.ia applique automatiquement cet ordre de priorité :'
                : 'Roland.ia automatically applies this priority order:'}
            </BodyText>
            <List>
              {[
                { fr: 'Perspective (Client Role) : Primaire > Secondaire > Tertiaire', en: 'Perspective (Client Role): Primary > Secondary > Tertiary' },
                { fr: 'Notoriété de l\'auteur : Très connu > Moyennement connu > Peu connu', en: 'Author Notoriety: Very well-known > Moderately known > Little known' },
                { fr: 'Sentiment : Extrêmes (très positifs/négatifs) > Autres', en: 'Sentiment: Extremes (very positive/negative) > Others' },
                { fr: 'Équilibre des marchés : Distribution proportionnelle des sources', en: 'Market Balance: Proportional distribution of sources' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`${index + 1}. ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 3 }} />

            <SectionTitle>2. {isFrench ? 'Fichiers CSV générés' : 'Generated CSV Files'}</SectionTitle>
            <BodyText>
              {isFrench
                ? 'Chaque sélection génère automatiquement un fichier CSV avec :'
                : 'Each selection automatically generates a CSV file with:'}
            </BodyText>
            <List>
              {[
                { fr: 'Métadonnées complètes : Titre, Source, Date, Auteur, Extrait', en: 'Complete metadata: Title, Source, Date, Author, Excerpt' },
                { fr: 'Critères de sélection : Rôle client, Ton, Notoriété, Langue, Type, Portée, Région', en: 'Selection criteria: Client role, Tone, Notoriety, Language, Type, Reach, Region' },
                { fr: 'Justification : Explication de la sélection pour chaque retombée', en: 'Justification: Explanation of the selection for each coverage' },
                { fr: 'Informations techniques : Fichier source, Numéro de page', en: 'Technical information: Source file, Page number' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SubSectionTitle>{isFrench ? 'Téléchargement' : 'Download'}</SubSectionTitle>
            <List>
              {[
                { fr: 'Un indicateur de chargement apparaît pendant la création du fichier', en: 'A loading indicator appears during file creation' },
                { fr: 'Un bouton de téléchargement s\'affiche une fois le fichier prêt', en: 'A download button displays once the file is ready' },
                { fr: 'Cliquez sur le bouton pour télécharger le CSV sur votre ordinateur', en: 'Click the button to download the CSV to your computer' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SubSectionTitle>{isFrench ? 'Enrichissement des données' : 'Data Enrichment'}</SubSectionTitle>
            <BodyText>
              {isFrench
                ? 'Roland.ia peut enrichir vos sélections en ajoutant des colonnes supplémentaires selon vos besoins :'
                : 'Roland.ia can enrich your selections by adding additional columns according to your needs:'}
            </BodyText>
            <SubSectionTitle>{isFrench ? 'Colonnes d\'enrichissement disponibles :' : 'Available enrichment columns:'}</SubSectionTitle>
            <List dense>
              {[
                { fr: 'Catégorie : Classification thématique des retombées (ex: "Économie", "Environnement", "Innovation")', en: 'Category: Thematic classification of coverage (e.g., "Economy", "Environment", "Innovation")' },
                { fr: 'Sous-catégorie : Détail supplémentaire de la catégorie', en: 'Sub-category: Additional category detail' },
                { fr: 'Impact : Niveau d\'impact de la retombée (ex: "Élevé", "Moyen", "Faible")', en: 'Impact: Impact level of the coverage (e.g., "High", "Medium", "Low")' },
                { fr: 'Mots-clés : Liste de mots-clés pertinents extraits de la retombée', en: 'Keywords: List of relevant keywords extracted from the coverage' },
                { fr: 'Sentiment détaillé : Analyse plus fine du sentiment (ex: "Très positif", "Légèrement négatif")', en: 'Detailed Sentiment: Finer sentiment analysis (e.g., "Very positive", "Slightly negative")' },
                { fr: 'Enjeu principal : Identification de l\'enjeu principal abordé dans la retombée', en: 'Main Issue: Identification of the main issue addressed in the coverage' },
                { fr: 'Stakeholders : Liste des parties prenantes mentionnées', en: 'Stakeholders: List of mentioned stakeholders' },
                { fr: 'Recommandation : Suggestion d\'action basée sur la retombée', en: 'Recommendation: Action suggestion based on the coverage' },
                { fr: 'Score de pertinence : Note numérique de pertinence pour le client', en: 'Relevance Score: Numerical relevance score for the client' },
                { fr: 'Date de publication formatée : Date dans différents formats (ex: "Q1 2025", "Janvier 2025")', en: 'Formatted Publication Date: Date in different formats (e.g., "Q1 2025", "January 2025")' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SubSectionTitle>{isFrench ? 'Comment demander un enrichissement' : 'How to Request Enrichment'}</SubSectionTitle>
            <BodyText>
              {isFrench
                ? 'Il suffit de mentionner les colonnes supplémentaires dans votre requête :'
                : 'Simply mention the additional columns in your query:'}
            </BodyText>
            <CodeBlock>
              {isFrench
                ? `✅ "25 retombées pour [Client] et ajoute une colonne Catégorie"
✅ "30 retombées avec colonnes Impact et Mots-clés"
✅ "20 retombées et enrichis avec Enjeu principal et Score de pertinence"`
                : `✅ "25 coverage for [Client] and add a Category column"
✅ "30 coverage with Impact and Keywords columns"
✅ "20 coverage and enrich with Main Issue and Relevance Score"`}
            </CodeBlock>

            <SubSectionTitle>{isFrench ? 'Exemples d\'enrichissement combiné' : 'Combined Enrichment Examples'}</SubSectionTitle>
            <CodeBlock>
              {isFrench
                ? `✅ "25 retombées pour [Client], ajoute les colonnes Catégorie, Impact et Recommandation"
✅ "30 retombées avec enrichissement complet : Catégorie, Sous-catégorie, Enjeu principal, Mots-clés"`
                : `✅ "25 coverage for [Client], add Category, Impact and Recommendation columns"
✅ "30 coverage with complete enrichment: Category, Sub-category, Main Issue, Keywords"`}
            </CodeBlock>

            <SubSectionTitle>{isFrench ? 'Enrichissement avec catégories existantes' : 'Enrichment with Existing Categories'}</SubSectionTitle>
            <BodyText>
              {isFrench
                ? 'Vous pouvez fournir vos propres catégories à Roland.ia pour l\'enrichissement :'
                : 'You can provide your own categories to Roland.ia for enrichment:'}
            </BodyText>
            <CodeBlock>
              {isFrench
                ? `✅ "25 retombées pour [Client] et ajoute une colonne Catégorie en utilisant ces catégories : Économie, Environnement, Innovation, Santé, Éducation"
✅ "30 retombées avec colonne Catégorie selon ces enjeux : Développement durable, Technologies, Finance, Transport"
✅ "20 retombées et classe-les dans ces catégories : Stratégie, Opérations, Relations publiques, Communication"`
                : `✅ "25 coverage for [Client] and add a Category column using these categories: Economy, Environment, Innovation, Health, Education"
✅ "30 coverage with Category column according to these issues: Sustainable Development, Technologies, Finance, Transport"
✅ "20 coverage and classify them in these categories: Strategy, Operations, Public Relations, Communication"`}
            </CodeBlock>

            <Divider sx={{ my: 3 }} />

            <SectionTitle>3. {isFrench ? 'Visualisations graphiques' : 'Graphical Visualizations'}</SectionTitle>
            <BodyText>
              {isFrench
                ? 'Roland.ia peut générer automatiquement des graphiques pour :'
                : 'Roland.ia can automatically generate charts for:'}
            </BodyText>
            <List>
              {[
                { fr: 'Distributions de ton (Très positif, Positif, Neutre, Négatif, Très négatif)', en: 'Tone distributions (Very positive, Positive, Neutral, Negative, Very negative)' },
                { fr: 'Répartition par source ou région', en: 'Distribution by source or region' },
                { fr: 'Évolution temporelle des retombées', en: 'Temporal evolution of coverage' },
                { fr: 'Comparaisons entre différents critères', en: 'Comparisons between different criteria' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>
            <BodyText>
              {isFrench
                ? 'Les graphiques s\'affichent directement dans la conversation et peuvent être téléchargés.'
                : 'Charts display directly in the conversation and can be downloaded.'}
            </BodyText>

            <Divider sx={{ my: 3 }} />

            <SectionTitle>4. {isFrench ? 'Gestion des conversations' : 'Conversation Management'}</SectionTitle>
            <List>
              {[
                { fr: 'Historique complet : Toutes vos conversations sont sauvegardées', en: 'Complete history: All your conversations are saved' },
                { fr: 'Reprise de contexte : Vous pouvez reprendre une conversation à tout moment', en: 'Context resumption: You can resume a conversation at any time' },
                { fr: 'Titres automatiques : Les conversations sont nommées automatiquement', en: 'Automatic titles: Conversations are automatically named' },
                { fr: 'Modification de titres : Cliquez sur le titre pour le modifier', en: 'Title modification: Click on the title to modify it' },
                { fr: 'Suppression : Supprimez des conversations individuelles ou toutes en une fois', en: 'Deletion: Delete individual conversations or all at once' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Best Practices - Continue in next part due to length */}
        {/* I'll continue with the rest of the sections... */}
        
        {/* Best Practices */}
        <Accordion expanded={expandedSection === 'best-practices'} onChange={handleChange('best-practices')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h5">✅ {isFrench ? 'Bonnes pratiques' : 'Best Practices'}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SectionTitle>1. {isFrench ? 'Formuler vos requêtes' : 'Formulating Your Queries'}</SectionTitle>
            
            <SubSectionTitle>{isFrench ? '✅ À faire' : '✅ Do'}</SubSectionTitle>
            <List>
              {[
                { fr: 'Soyez spécifique : "25 retombées pour le Port de Montréal avec ton positif"', en: 'Be specific: "25 coverage for Port of Montreal with positive tone"' },
                { fr: 'Indiquez le nombre : "Donne-moi 30 retombées" plutôt que "beaucoup de retombées"', en: 'Indicate the number: "Give me 30 coverage" rather than "many coverage"' },
                { fr: 'Précisez les critères : "Retombées primaires avec auteurs très connus"', en: 'Specify criteria: "Primary coverage with very well-known authors"' },
                { fr: 'Utilisez des exemples : "Comme la dernière fois, mais avec ton négatif"', en: 'Use examples: "Like last time, but with negative tone"' },
                { fr: 'Réitérez les informations clés : Si vous changez de client ou de sujet, mentionnez-le explicitement', en: 'Reiterate key information: If you change client or subject, mention it explicitly' },
                { fr: 'Une requête à la fois : Évitez les requêtes trop complexes avec plusieurs conditions enchaînées', en: 'One query at a time: Avoid overly complex queries with multiple chained conditions' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <CheckIcon color="success" sx={{ mr: 1.5, flexShrink: 0 }} />
                  <ListItemText 
                    primary={isFrench ? item.fr : item.en}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SubSectionTitle>{isFrench ? '❌ À éviter' : '❌ Don\'t'}</SubSectionTitle>
            <List>
              {[
                { fr: 'Vagues : "Des retombées intéressantes"', en: 'Vague: "Interesting coverage"' },
                { fr: 'Trop de critères contradictoires : "25 retombées positives et négatives"', en: 'Too many contradictory criteria: "25 positive and negative coverage"' },
                { fr: 'Demandes impossibles : "1000 retombées pour un petit client"', en: 'Impossible requests: "1000 coverage for a small client"' },
                { fr: 'Requêtes trop complexes : Évitez d\'enchaîner plusieurs conditions complexes en une seule requête', en: 'Overly complex queries: Avoid chaining multiple complex conditions in a single query' },
                { fr: 'Changements de contexte sans clarification : Si vous changez de sujet, mentionnez-le explicitement', en: 'Context changes without clarification: If you change subject, mention it explicitly' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <CancelIcon color="error" sx={{ mr: 1.5, flexShrink: 0 }} />
                  <ListItemText 
                    primary={isFrench ? item.fr : item.en}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SectionTitle>2. {isFrench ? 'Optimiser vos sélections' : 'Optimizing Your Selections'}</SectionTitle>
            <SubSectionTitle>{isFrench ? 'Stratégies recommandées' : 'Recommended Strategies'}</SubSectionTitle>
            <List>
              {[
                { fr: 'Commencez large, puis affinez : Première requête "50 retombées pour [Client]", puis "Parmi celles-là, garde seulement les primaires"', en: 'Start broad, then refine: First query "50 coverage for [Client]", then "Among those, keep only the primary ones"' },
                { fr: 'Utilisez les critères par ordre d\'importance : Priorité 1 Perspective, Priorité 2 Notoriété, Priorité 3 Ton', en: 'Use criteria in order of importance: Priority 1 Perspective, Priority 2 Notoriety, Priority 3 Tone' },
                { fr: 'Demandez des visualisations : "Montre-moi la distribution des tons", "Fais un graphique par région"', en: 'Request visualizations: "Show me the tone distribution", "Make a chart by region"' },
                { fr: 'Vérifiez les justifications : Lisez les justifications dans le CSV pour comprendre les sélections', en: 'Check justifications: Read justifications in the CSV to understand selections' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`${index + 1}. ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SectionTitle>3. {isFrench ? 'Gérer vos fichiers CSV' : 'Managing Your CSV Files'}</SectionTitle>
            <SubSectionTitle>{isFrench ? 'Organisation' : 'Organization'}</SubSectionTitle>
            <List>
              {[
                { fr: 'Téléchargez immédiatement : Les fichiers sont disponibles dès qu\'ils sont prêts', en: 'Download immediately: Files are available as soon as they\'re ready' },
                { fr: 'Nommez vos conversations : Utilisez des titres descriptifs', en: 'Name your conversations: Use descriptive titles' },
                { fr: 'Archivez vos sélections : Gardez une trace de vos requêtes importantes', en: 'Archive your selections: Keep a record of your important queries' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SubSectionTitle>{isFrench ? 'Format du CSV' : 'CSV Format'}</SubSectionTitle>
            <List>
              {[
                { fr: 'Encodage : UTF-8 avec BOM (compatible Excel)', en: 'Encoding: UTF-8 with BOM (Excel compatible)' },
                { fr: 'Séparateur : Point-virgule (;)', en: 'Separator: Semicolon (;)' },
                { fr: 'Colonnes fixes : Ne modifiez pas l\'ordre des colonnes', en: 'Fixed columns: Don\'t modify column order' },
                { fr: 'Justifications : Contiennent des guillemets si nécessaire', en: 'Justifications: Contain quotes if necessary' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SectionTitle>4. {isFrench ? 'Utiliser les conversations' : 'Using Conversations'}</SectionTitle>
            <SubSectionTitle>{isFrench ? 'Bonnes pratiques' : 'Best Practices'}</SubSectionTitle>
            <List>
              {[
                { fr: 'Une conversation par projet : Créez une nouvelle conversation pour chaque client/projet', en: 'One conversation per project: Create a new conversation for each client/project' },
                { fr: 'Continuez les conversations : Roland.ia se souvient du contexte précédent', en: 'Continue conversations: Roland.ia remembers previous context' },
                { fr: 'Référencez les sélections précédentes : "Comme la sélection précédente, mais..."', en: 'Reference previous selections: "Like the previous selection, but..."' },
                { fr: 'Maintenez un contexte clair : Si une conversation devient trop longue (20-30 messages), créez une nouvelle conversation', en: 'Maintain clear context: If a conversation becomes too long (20-30 messages), create a new conversation' },
                { fr: 'Réitérez les informations clés : Si vous changez de sujet ou de client, rappelez-le explicitement à Roland.ia', en: 'Reiterate key information: If you change subject or client, remind Roland.ia explicitly' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SubSectionTitle>{isFrench ? 'Gestion' : 'Management'}</SubSectionTitle>
            <List>
              {[
                { fr: 'Supprimez les conversations terminées : Gardez votre liste propre', en: 'Delete completed conversations: Keep your list clean' },
                { fr: 'Utilisez des titres clairs : "Port de Montréal - Sélection Q1 2025"', en: 'Use clear titles: "Port of Montreal - Q1 2025 Selection"' },
                { fr: 'Archivez avant suppression : Téléchargez les CSV importants', en: 'Archive before deletion: Download important CSVs' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SectionTitle>5. {isFrench ? 'Enrichir vos sélections' : 'Enriching Your Selections'}</SectionTitle>
            <SubSectionTitle>{isFrench ? 'Quand utiliser l\'enrichissement' : 'When to Use Enrichment'}</SubSectionTitle>
            <BodyText>
              {isFrench
                ? 'L\'enrichissement est particulièrement utile pour :'
                : 'Enrichment is particularly useful for:'}
            </BodyText>
            <List>
              {[
                { fr: 'Analyse thématique : Utilisez "Catégorie" et "Sous-catégorie" pour organiser vos retombées par enjeux', en: 'Thematic analysis: Use "Category" and "Sub-category" to organize your coverage by issues' },
                { fr: 'Priorisation : Utilisez "Impact" et "Score de pertinence" pour identifier les retombées les plus importantes', en: 'Prioritization: Use "Impact" and "Relevance Score" to identify the most important coverage' },
                { fr: 'Analyse de sentiment fine : Utilisez "Sentiment détaillé" pour une analyse plus nuancée', en: 'Fine sentiment analysis: Use "Detailed Sentiment" for a more nuanced analysis' },
                { fr: 'Identification des parties prenantes : Utilisez "Stakeholders" pour voir qui est mentionné dans les retombées', en: 'Stakeholder identification: Use "Stakeholders" to see who is mentioned in the coverage' },
                { fr: 'Planification d\'actions : Utilisez "Recommandation" pour obtenir des suggestions d\'actions', en: 'Action planning: Use "Recommendation" to get action suggestions' },
                { fr: 'Recherche et référencement : Utilisez "Mots-clés" pour faciliter la recherche ultérieure', en: 'Search and referencing: Use "Keywords" to facilitate later searches' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SubSectionTitle>{isFrench ? 'Stratégies d\'enrichissement' : 'Enrichment Strategies'}</SubSectionTitle>
            <List>
              {[
                { fr: 'Commencez simple, puis enrichissez : Première requête "25 retombées pour [Client]", puis "Ajoute une colonne Catégorie"', en: 'Start simple, then enrich: First query "25 coverage for [Client]", then "Add a Category column"' },
                { fr: 'Enrichissez selon votre besoin : Pour un rapport thématique (Catégorie + Enjeu principal), pour une analyse d\'impact (Impact + Score de pertinence)', en: 'Enrich according to your need: For a thematic report (Category + Main Issue), for an impact analysis (Impact + Relevance Score)' },
                { fr: 'Combinez enrichissement et visualisation : "25 retombées avec colonne Catégorie et montre-moi la distribution"', en: 'Combine enrichment and visualization: "25 coverage with Category column and show me the distribution"' },
                { fr: 'Réutilisez les enrichissements : Une fois une colonne ajoutée, vous pouvez la référencer dans les requêtes suivantes', en: 'Reuse enrichments: Once a column is added, you can reference it in subsequent queries' },
                { fr: 'Utilisez vos propres catégories : Fournissez vos catégories existantes pour un enrichissement cohérent avec votre classification interne', en: 'Use your own categories: Provide your existing categories for enrichment consistent with your internal classification' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`${index + 1}. ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SectionTitle>6. {isFrench ? 'Comprendre les résultats' : 'Understanding Results'}</SectionTitle>
            <SubSectionTitle>{isFrench ? 'Métadonnées importantes' : 'Important Metadata'}</SubSectionTitle>
            
            <SubSectionTitle>{isFrench ? 'Client Role (Perspective) :' : 'Client Role (Perspective):'}</SubSectionTitle>
            <List dense>
              {[
                { fr: 'Primaire = Le client est l\'enjeu principal', en: 'Primary = The client is the main issue' },
                { fr: 'Secondaire = Le client est mentionné mais pas l\'enjeu principal', en: 'Secondary = The client is mentioned but not the main issue' },
                { fr: 'Tertiaire = Mention mineure', en: 'Tertiary = Minor mention' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SubSectionTitle>{isFrench ? 'Tone (Sentiment) :' : 'Tone (Sentiment):'}</SubSectionTitle>
            <List dense>
              {[
                { fr: 'Très positif = Retombée très favorable au client', en: 'Very positive = Coverage very favorable to the client' },
                { fr: 'Positif = Retombée favorable au client', en: 'Positive = Coverage favorable to the client' },
                { fr: 'Neutre = Retombée factuelle sans opinion marquée', en: 'Neutral = Factual coverage without marked opinion' },
                { fr: 'Négatif = Retombée défavorable au client', en: 'Negative = Coverage unfavorable to the client' },
                { fr: 'Très négatif = Retombée très défavorable au client', en: 'Very negative = Coverage very unfavorable to the client' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SubSectionTitle>{isFrench ? 'Notoriété :' : 'Notoriety:'}</SubSectionTitle>
            <List dense>
              {[
                { fr: 'Très connu = Auteur/source de grande renommée', en: 'Very well-known = Author/source of great renown' },
                { fr: 'Moyennement connu = Auteur/source reconnu', en: 'Moderately known = Recognized author/source' },
                { fr: 'Peu connu = Auteur/source moins connu', en: 'Little known = Less known author/source' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SubSectionTitle>{isFrench ? 'Interpréter les justifications' : 'Interpreting Justifications'}</SubSectionTitle>
            <BodyText>
              {isFrench
                ? 'Chaque justification explique :'
                : 'Each justification explains:'}
            </BodyText>
            <List>
              {[
                { fr: 'Pourquoi la retombée a été sélectionnée', en: 'Why the coverage was selected' },
                { fr: 'Quels critères ont été appliqués', en: 'What criteria were applied' },
                { fr: 'La pertinence pour votre demande', en: 'Relevance for your request' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Examples - Continue in next message due to length limit */}
        {/* I'll add the remaining sections: Examples, Troubleshooting, FAQ, Advanced Tips */}
        
        {/* Examples Section */}
        <Accordion expanded={expandedSection === 'examples'} onChange={handleChange('examples')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h5">📝 {isFrench ? 'Exemples de requêtes' : 'Query Examples'}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SectionTitle>{isFrench ? 'Exemples basiques' : 'Basic Examples'}</SectionTitle>
            <CodeBlock>
              {isFrench
                ? `✅ "Donne-moi 25 retombées pour le Port de Montréal"
✅ "Sélectionne 30 retombées positives pour [Client]"
✅ "Je veux 20 retombées primaires avec auteurs très connus"`
                : `✅ "Give me 25 coverage for Port of Montreal"
✅ "Select 30 positive coverage for [Client]"
✅ "I want 20 primary coverage with very well-known authors"`}
            </CodeBlock>

            <SectionTitle>{isFrench ? 'Exemples avancés' : 'Advanced Examples'}</SectionTitle>
            <CodeBlock>
              {isFrench
                ? `✅ "25 retombées pour le Port de Montréal, ton positif, perspective primaire, région Montréal"
✅ "30 retombées pour [Client], équilibre entre positif et négatif, sources très connues"
✅ "20 retombées primaires, ton neutre, répartition équilibrée par région"
✅ "25 retombées pour [Client] publiées en janvier 2025"
✅ "30 retombées pour [Client] entre janvier et mars 2025"
✅ "20 retombées pour [Client] des 6 derniers mois"
✅ "25 retombées pour [Client] publiées après le 1er janvier 2025"`
                : `✅ "25 coverage for Port of Montreal, positive tone, primary perspective, Montreal region"
✅ "30 coverage for [Client], balance between positive and negative, very well-known sources"
✅ "20 primary coverage, neutral tone, balanced distribution by region"
✅ "25 coverage for [Client] published in January 2025"
✅ "30 coverage for [Client] between January and March 2025"
✅ "20 coverage for [Client] from the last 6 months"
✅ "25 coverage for [Client] published after January 1, 2025"`}
            </CodeBlock>

            <SectionTitle>{isFrench ? 'Exemples avec visualisations' : 'Examples with Visualizations'}</SectionTitle>
            <CodeBlock>
              {isFrench
                ? `✅ "25 retombées pour [Client] et montre-moi la distribution des tons"
✅ "Sélectionne 30 retombées et fais un graphique par source"
✅ "20 retombées avec un graphique de l'évolution temporelle"`
                : `✅ "25 coverage for [Client] and show me the tone distribution"
✅ "Select 30 coverage and make a chart by source"
✅ "20 coverage with a temporal evolution chart"`}
            </CodeBlock>

            <SectionTitle>{isFrench ? 'Exemples de raffinement' : 'Refinement Examples'}</SectionTitle>
            <CodeBlock>
              {isFrench
                ? `✅ "Parmi les 50 retombées précédentes, garde seulement les primaires"
✅ "Refais la sélection mais avec ton négatif cette fois"
✅ "Même sélection mais seulement les auteurs très connus"`
                : `✅ "Among the 50 previous coverage, keep only the primary ones"
✅ "Redo the selection but with negative tone this time"
✅ "Same selection but only very well-known authors"`}
            </CodeBlock>

            <SectionTitle>{isFrench ? 'Exemples avec enrichissement de données' : 'Examples with Data Enrichment'}</SectionTitle>
            <CodeBlock>
              {isFrench
                ? `✅ "25 retombées pour [Client] et ajoute une colonne Catégorie"
✅ "30 retombées avec colonnes Impact et Mots-clés"
✅ "20 retombées et enrichis avec Enjeu principal et Score de pertinence"
✅ "25 retombées pour [Client], ajoute les colonnes Catégorie, Impact et Recommandation"
✅ "30 retombées avec enrichissement complet : Catégorie, Sous-catégorie, Enjeu principal, Mots-clés"
✅ "20 retombées primaires et ajoute une colonne Stakeholders pour voir qui est mentionné"
✅ "25 retombées avec colonnes Sentiment détaillé et Date de publication formatée"
✅ "25 retombées pour [Client] publiées en janvier 2025 et ajoute une colonne Catégorie"
✅ "30 retombées pour [Client] entre janvier et mars 2025 avec colonnes Impact et Mots-clés"
✅ "20 retombées des 6 derniers mois et enrichis avec Enjeu principal et Score de pertinence"`
                : `✅ "25 coverage for [Client] and add a Category column"
✅ "30 coverage with Impact and Keywords columns"
✅ "20 coverage and enrich with Main Issue and Relevance Score"
✅ "25 coverage for [Client], add Category, Impact and Recommendation columns"
✅ "30 coverage with complete enrichment: Category, Sub-category, Main Issue, Keywords"
✅ "20 primary coverage and add a Stakeholders column to see who is mentioned"
✅ "25 coverage with Detailed Sentiment and Formatted Publication Date columns"
✅ "25 coverage for [Client] published in January 2025 and add a Category column"
✅ "30 coverage for [Client] between January and March 2025 with Impact and Keywords columns"
✅ "20 coverage from the last 6 months and enrich with Main Issue and Relevance Score"`}
            </CodeBlock>

            <SectionTitle>{isFrench ? 'Exemples avec enrichissement et catégories personnalisées' : 'Examples with Enrichment and Custom Categories'}</SectionTitle>
            <CodeBlock>
              {isFrench
                ? `✅ "25 retombées pour [Client] et ajoute une colonne Catégorie en utilisant ces catégories : Économie, Environnement, Innovation, Santé"
✅ "30 retombées pour [Client] publiées en 2025, classe-les dans ces catégories : Développement durable, Technologies, Finance, Transport"
✅ "20 retombées pour [Client] et ajoute une colonne Catégorie selon ces enjeux : Stratégie, Opérations, Relations publiques, Communication"
✅ "25 retombées pour [Client] des 6 derniers mois avec colonne Catégorie (Économie, Environnement, Innovation) et colonne Impact"`
                : `✅ "25 coverage for [Client] and add a Category column using these categories: Economy, Environment, Innovation, Health"
✅ "30 coverage for [Client] published in 2025, classify them in these categories: Sustainable Development, Technologies, Finance, Transport"
✅ "20 coverage for [Client] and add a Category column according to these issues: Strategy, Operations, Public Relations, Communication"
✅ "25 coverage for [Client] from the last 6 months with Category column (Economy, Environment, Innovation) and Impact column"`}
            </CodeBlock>

            <SectionTitle>{isFrench ? 'Exemples combinés (sélection + enrichissement + visualisation)' : 'Combined Examples (selection + enrichment + visualization)'}</SectionTitle>
            <CodeBlock>
              {isFrench
                ? `✅ "25 retombées pour [Client], ajoute une colonne Catégorie et montre-moi la distribution par catégorie"
✅ "30 retombées avec colonnes Impact et Mots-clés, puis fais un graphique de l'impact"
✅ "20 retombées primaires avec enrichissement Enjeu principal et visualise les enjeux les plus fréquents"`
                : `✅ "25 coverage for [Client], add a Category column and show me the distribution by category"
✅ "30 coverage with Impact and Keywords columns, then make an impact chart"
✅ "20 primary coverage with Main Issue enrichment and visualize the most frequent issues"`}
            </CodeBlock>
          </AccordionDetails>
        </Accordion>

        {/* Troubleshooting - Very detailed section */}
        <Accordion expanded={expandedSection === 'troubleshooting'} onChange={handleChange('troubleshooting')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h5">🔧 {isFrench ? 'Résolution de problèmes' : 'Troubleshooting'}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SectionTitle>{isFrench ? 'Problème : Aucune retombée trouvée' : 'Problem: No Coverage Found'}</SectionTitle>
            <SubSectionTitle>{isFrench ? 'Solutions :' : 'Solutions:'}</SubSectionTitle>
            <SolutionList items={[
              { fr: 'Vérifiez l\'orthographe du nom du client', en: 'Check the spelling of the client name' },
              { fr: 'Élargissez vos critères (enlevez des restrictions)', en: 'Broaden your criteria (remove restrictions)' },
              { fr: 'Essayez avec un nombre de retombées plus petit', en: 'Try with a smaller number of coverage' },
              { fr: 'Vérifiez que le client existe dans la base de données', en: 'Verify that the client exists in the database' },
            ]} />

            <SectionTitle>{isFrench ? 'Problème : Trop peu de retombées' : 'Problem: Too Few Coverage'}</SectionTitle>
            <SubSectionTitle>{isFrench ? 'Solutions :' : 'Solutions:'}</SubSectionTitle>
            <SolutionList items={[
              { fr: 'Réduisez le nombre de retombées demandé', en: 'Reduce the number of coverage requested' },
              { fr: 'Élargissez les critères (enlevez "très connu", changez "primaire" en "primaire ou secondaire")', en: 'Broaden criteria (remove "very well-known", change "primary" to "primary or secondary")' },
              { fr: 'Vérifiez les justifications pour comprendre les limitations', en: 'Check justifications to understand limitations' },
            ]} />

            <SectionTitle>{isFrench ? 'Problème : Fichier CSV ne se télécharge pas' : 'Problem: CSV File Won\'t Download'}</SectionTitle>
            <SubSectionTitle>{isFrench ? 'Solutions :' : 'Solutions:'}</SubSectionTitle>
            <SolutionList items={[
              { fr: 'Attendez que l\'indicateur de chargement disparaisse', en: 'Wait for the loading indicator to disappear' },
              { fr: 'Vérifiez votre connexion internet', en: 'Check your internet connection' },
              { fr: 'Essayez de rafraîchir la page', en: 'Try refreshing the page' },
              { fr: 'Vérifiez que votre navigateur autorise les téléchargements', en: 'Verify that your browser allows downloads' },
            ]} />

            <SectionTitle>{isFrench ? 'Problème : Graphique ne s\'affiche pas' : 'Problem: Chart Won\'t Display'}</SectionTitle>
            <SubSectionTitle>{isFrench ? 'Solutions :' : 'Solutions:'}</SubSectionTitle>
            <SolutionList items={[
              { fr: 'Attendez quelques secondes (les graphiques peuvent prendre du temps)', en: 'Wait a few seconds (charts can take time)' },
              { fr: 'Vérifiez votre connexion internet', en: 'Check your internet connection' },
              { fr: 'Essayez de demander le graphique à nouveau', en: 'Try requesting the chart again' },
              { fr: 'Vérifiez que vous avez demandé une visualisation appropriée', en: 'Verify that you requested an appropriate visualization' },
            ]} />

            <SectionTitle>{isFrench ? 'Problème : Conversation perdue' : 'Problem: Lost Conversation'}</SectionTitle>
            <SubSectionTitle>{isFrench ? 'Solutions :' : 'Solutions:'}</SubSectionTitle>
            <SolutionList items={[
              { fr: 'Les conversations sont sauvegardées automatiquement', en: 'Conversations are automatically saved' },
              { fr: 'Vérifiez votre liste de conversations', en: 'Check your conversation list' },
              { fr: 'Les conversations sont nommées automatiquement (cherchez par date)', en: 'Conversations are automatically named (search by date)' },
              { fr: 'Contactez un administrateur si le problème persiste', en: 'Contact an administrator if the problem persists' },
            ]} />

            <SectionTitle>{isFrench ? 'Problème : Roland.ia perd le fil ou a des difficultés à suivre le raisonnement' : 'Problem: Roland.ia Loses Track or Has Difficulty Following Reasoning'}</SectionTitle>
            <BodyText>
              {isFrench
                ? 'Parfois, Roland.ia peut perdre le contexte d\'une conversation ou avoir de la difficulté à retrouver des données mentionnées précédemment. Voici comment résoudre ces situations :'
                : 'Sometimes, Roland.ia can lose the context of a conversation or have difficulty retrieving previously mentioned data. Here\'s how to resolve these situations:'}
            </BodyText>

            <SubSectionTitle>{isFrench ? 'Symptômes courants' : 'Common Symptoms'}</SubSectionTitle>
            <List>
              {[
                { fr: 'Roland.ia dit qu\'il ne trouve pas de données alors qu\'elles existent', en: 'Roland.ia says it can\'t find data when it exists' },
                { fr: 'Roland.ia oublie des informations mentionnées dans la conversation précédente', en: 'Roland.ia forgets information mentioned in previous conversation' },
                { fr: 'Roland.ia répète des questions déjà posées', en: 'Roland.ia repeats questions already asked' },
                { fr: 'Les résultats sont incohérents avec les requêtes précédentes', en: 'Results are inconsistent with previous queries' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SubSectionTitle>{isFrench ? 'Solutions immédiates' : 'Immediate Solutions'}</SubSectionTitle>
            <SubSectionTitle>1. {isFrench ? 'Rafraîchir le contexte' : 'Refresh the context'}</SubSectionTitle>
            <CodeBlock>
              {isFrench
                ? `✅ "Rappelle-toi : nous cherchons des retombées pour [Client]"
✅ "Reprends depuis le début : je veux 25 retombées pour [Client]"`
                : `✅ "Remember: we're looking for coverage for [Client]"
✅ "Start over: I want 25 coverage for [Client]"`}
            </CodeBlock>

            <SubSectionTitle>2. {isFrench ? 'Réitérer les informations clés' : 'Reiterate key information'}</SubSectionTitle>
            <CodeBlock>
              {isFrench
                ? `✅ "Pour [Client], sélectionne 30 retombées primaires avec ton positif"
✅ "Comme je t'ai dit précédemment, cherche pour [Client] dans la base de données"`
                : `✅ "For [Client], select 30 primary coverage with positive tone"
✅ "As I told you previously, search for [Client] in the database"`}
            </CodeBlock>

            <SubSectionTitle>3. {isFrench ? 'Simplifier la requête' : 'Simplify the query'}</SubSectionTitle>
            <CodeBlock>
              {isFrench
                ? `✅ Commencez par une requête simple : "25 retombées pour [Client]"
✅ Puis affinez progressivement : "Parmi celles-là, garde seulement les primaires"`
                : `✅ Start with a simple query: "25 coverage for [Client]"
✅ Then refine progressively: "Among those, keep only the primary ones"`}
            </CodeBlock>

            <SubSectionTitle>4. {isFrench ? 'Utiliser des références explicites' : 'Use explicit references'}</SubSectionTitle>
            <CodeBlock>
              {isFrench
                ? `✅ "Dans la sélection précédente de 50 retombées, garde seulement les 25 meilleures"
✅ "Refais la même recherche que tout à l'heure mais avec ton négatif"`
                : `✅ "In the previous selection of 50 coverage, keep only the 25 best"
✅ "Redo the same search as earlier but with negative tone"`}
            </CodeBlock>

            <SubSectionTitle>5. {isFrench ? 'Recréer la sélection' : 'Recreate the selection'}</SubSectionTitle>
            <CodeBlock>
              {isFrench
                ? `✅ Si Roland.ia semble complètement perdu, recréez la sélection depuis le début
✅ "Oublie ce qui précède, je recommence : 25 retombées pour [Client]"`
                : `✅ If Roland.ia seems completely lost, recreate the selection from scratch
✅ "Forget what preceded, I'm starting over: 25 coverage for [Client]"`}
            </CodeBlock>

            <SubSectionTitle>{isFrench ? 'Stratégies préventives' : 'Preventive Strategies'}</SubSectionTitle>
            <List>
              {[
                { fr: 'Une requête à la fois : Évitez les requêtes trop complexes avec plusieurs conditions', en: 'One query at a time: Avoid overly complex queries with multiple conditions' },
                { fr: 'Confirmez les résultats : Vérifiez que Roland.ia a bien compris votre demande avant de continuer', en: 'Confirm results: Verify that Roland.ia understood your request before continuing' },
                { fr: 'Référencez explicitement : Mentionnez clairement les sélections précédentes', en: 'Reference explicitly: Clearly mention previous selections' },
                { fr: 'Nouvelle conversation si nécessaire : Si la conversation devient trop longue ou confuse, créez une nouvelle conversation', en: 'New conversation if necessary: If the conversation becomes too long or confusing, create a new conversation' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`${index + 1}. ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <SubSectionTitle>{isFrench ? 'Exemples de récupération' : 'Recovery Examples'}</SubSectionTitle>
            
            <SubSectionTitle>{isFrench ? 'Cas 1 : Roland.ia ne trouve pas les données' : 'Case 1: Roland.ia Can\'t Find Data'}</SubSectionTitle>
            <CodeBlock>
              {isFrench
                ? `❌ Problème : "Je ne trouve pas de retombées pour [Client]"
✅ Solution : "Cherche dans toute la base de données pour [Client], il devrait y en avoir"
✅ Alternative : "Vérifie l'orthographe : [Client avec orthographe exacte]"`
                : `❌ Problem: "I can't find coverage for [Client]"
✅ Solution: "Search the entire database for [Client], there should be some"
✅ Alternative: "Check spelling: [Client with exact spelling]"`}
            </CodeBlock>

            <SubSectionTitle>{isFrench ? 'Cas 2 : Roland.ia oublie le contexte' : 'Case 2: Roland.ia Forgets Context'}</SubSectionTitle>
            <CodeBlock>
              {isFrench
                ? `❌ Problème : Roland.ia demande à nouveau le nom du client
✅ Solution : "Comme je t'ai dit, c'est pour [Client]. Continue avec cette sélection"`
                : `❌ Problem: Roland.ia asks for the client name again
✅ Solution: "As I told you, it's for [Client]. Continue with this selection"`}
            </CodeBlock>

            <SubSectionTitle>{isFrench ? 'Cas 3 : Résultats incohérents' : 'Case 3: Inconsistent Results'}</SubSectionTitle>
            <CodeBlock>
              {isFrench
                ? `❌ Problème : Les résultats ne correspondent pas à la requête précédente
✅ Solution : "Refais la sélection exactement comme la précédente : 25 retombées primaires pour [Client]"`
                : `❌ Problem: Results don't match the previous query
✅ Solution: "Redo the selection exactly like the previous one: 25 primary coverage for [Client]"`}
            </CodeBlock>

            <SubSectionTitle>{isFrench ? 'Cas 4 : Perte de fil dans un raisonnement complexe' : 'Case 4: Loss of Track in Complex Reasoning'}</SubSectionTitle>
            <CodeBlock>
              {isFrench
                ? `❌ Problème : Roland.ia semble confus après plusieurs étapes
✅ Solution : "Résume ce que nous avons fait jusqu'à présent, puis continue"
✅ Alternative : "Recommence depuis le début avec cette requête : [requête complète]"`
                : `❌ Problem: Roland.ia seems confused after several steps
✅ Solution: "Summarize what we've done so far, then continue"
✅ Alternative: "Start over from the beginning with this query: [complete query]"`}
            </CodeBlock>

            <SubSectionTitle>{isFrench ? 'Quand créer une nouvelle conversation' : 'When to Create a New Conversation'}</SubSectionTitle>
            <BodyText>
              {isFrench
                ? 'Créez une nouvelle conversation si :'
                : 'Create a new conversation if:'}
            </BodyText>
            <List>
              {[
                { fr: 'La conversation actuelle dépasse 20-30 messages', en: 'The current conversation exceeds 20-30 messages' },
                { fr: 'Roland.ia répète constamment les mêmes erreurs', en: 'Roland.ia repeatedly makes the same errors' },
                { fr: 'Le contexte semble complètement perdu', en: 'Context seems completely lost' },
                { fr: 'Vous changez de sujet ou de client', en: 'You change subject or client' },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <Alert severity="info" sx={{ mt: 2 }}>
              <AlertTitle>{isFrench ? 'Avantage d\'une nouvelle conversation' : 'Advantage of a New Conversation'}</AlertTitle>
              <List dense>
                {[
                  { fr: 'Contexte propre et clair', en: 'Clean and clear context' },
                  { fr: 'Pas de confusion avec les messages précédents', en: 'No confusion with previous messages' },
                  { fr: 'Performance généralement meilleure', en: 'Generally better performance' },
                ].map((item, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                    primary={`• ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                  </ListItem>
                ))}
              </List>
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* FAQ - Complete section */}
        <Accordion expanded={expandedSection === 'faq'} onChange={handleChange('faq')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h5">❓ FAQ</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SectionTitle>{isFrench ? 'Questions générales' : 'General Questions'}</SectionTitle>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Combien de retombées puis-je demander ?' : 'Q: How many coverage can I request?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Il n\'y a pas de limite stricte, mais nous recommandons entre 10 et 100 retombées pour des résultats optimaux.'
                  : 'A: There\'s no strict limit, but we recommend between 10 and 100 coverage for optimal results.'}
              </BodyText>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Les données sont-elles à jour ?' : 'Q: Is the data up to date?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Oui, Roland.ia utilise la base de données la plus récente disponible.'
                  : 'A: Yes, Roland.ia uses the most recent database available.'}
              </BodyText>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Puis-je modifier une sélection après l\'avoir créée ?' : 'Q: Can I modify a selection after creating it?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Non, mais vous pouvez créer une nouvelle sélection en référençant la précédente ("Comme la sélection précédente, mais...").'
                  : 'A: No, but you can create a new selection by referencing the previous one ("Like the previous selection, but...").'}
              </BodyText>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Les fichiers CSV sont-ils compatibles avec Excel ?' : 'Q: Are CSV files compatible with Excel?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Oui, les fichiers sont encodés en UTF-8 avec BOM et utilisent le point-virgule comme séparateur, ce qui est compatible avec Excel.'
                  : 'A: Yes, files are encoded in UTF-8 with BOM and use semicolon as separator, which is Excel compatible.'}
              </BodyText>
            </Box>

            <SectionTitle>{isFrench ? 'Questions techniques' : 'Technical Questions'}</SectionTitle>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Combien de temps prend la génération d\'un fichier CSV ?' : 'Q: How long does CSV file generation take?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Généralement entre 10 et 60 secondes selon le nombre de retombées et la complexité de la requête.'
                  : 'A: Generally between 10 and 60 seconds depending on the number of coverage and query complexity.'}
              </BodyText>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Puis-je télécharger plusieurs fois le même fichier ?' : 'Q: Can I download the same file multiple times?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Oui, le fichier reste disponible dans la conversation tant que vous ne la supprimez pas.'
                  : 'A: Yes, the file remains available in the conversation as long as you don\'t delete it.'}
              </BodyText>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Les graphiques sont-ils inclus dans le CSV ?' : 'Q: Are charts included in the CSV?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Non, les graphiques sont des images séparées qui s\'affichent dans la conversation. Vous pouvez les télécharger individuellement.'
                  : 'A: No, charts are separate images that display in the conversation. You can download them individually.'}
              </BodyText>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Puis-je exporter mes conversations ?' : 'Q: Can I export my conversations?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Actuellement, seuls les fichiers CSV peuvent être téléchargés. L\'export complet des conversations sera disponible dans une future version.'
                  : 'A: Currently, only CSV files can be downloaded. Complete conversation export will be available in a future version.'}
              </BodyText>
            </Box>

            <SectionTitle>{isFrench ? 'Questions sur les critères' : 'Questions About Criteria'}</SectionTitle>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Que signifie "Perspective Primaire" ?' : 'Q: What does "Primary Perspective" mean?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Cela signifie que le client est l\'enjeu principal de la retombée, pas juste une mention en passant.'
                  : 'A: This means the client is the main issue of the coverage, not just a passing mention.'}
              </BodyText>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Comment Roland.ia équilibre-t-il les tons ?' : 'Q: How does Roland.ia balance tones?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Roland.ia essaie de respecter une distribution équilibrée entre Très positif, Positif, Neutre, Négatif et Très négatif, sauf si vous spécifiez un ton particulier.'
                  : 'A: Roland.ia tries to respect a balanced distribution between Very positive, Positive, Neutral, Negative and Very negative, unless you specify a particular tone.'}
              </BodyText>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Puis-je demander des retombées d\'une période spécifique ?' : 'Q: Can I request coverage from a specific period?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Oui, vous pouvez mentionner une période dans votre requête (ex: "retombées de janvier 2025", "retombées entre janvier et mars 2025", "retombées des 6 derniers mois"). Cela dépend de la disponibilité des données dans la base et des dates de publication des retombées.'
                  : 'A: Yes, you can mention a period in your query (e.g., "coverage from January 2025", "coverage between January and March 2025", "coverage from the last 6 months"). This depends on data availability in the database and the publication dates of the coverage.'}
              </BodyText>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Puis-je ajouter des colonnes personnalisées à mes sélections ?' : 'Q: Can I add custom columns to my selections?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Oui ! Vous pouvez demander à Roland.ia d\'ajouter des colonnes d\'enrichissement comme Catégorie, Impact, Mots-clés, Enjeu principal, etc. Mentionnez simplement les colonnes souhaitées dans votre requête.'
                  : 'A: Yes! You can ask Roland.ia to add enrichment columns like Category, Impact, Keywords, Main Issue, etc. Simply mention the desired columns in your query.'}
              </BodyText>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Combien de colonnes d\'enrichissement puis-je ajouter ?' : 'Q: How many enrichment columns can I add?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Vous pouvez ajouter plusieurs colonnes d\'enrichissement en une seule requête. Il n\'y a pas de limite stricte, mais nous recommandons de ne pas dépasser 5-6 colonnes pour garder le fichier CSV lisible.'
                  : 'A: You can add several enrichment columns in a single query. There\'s no strict limit, but we recommend not exceeding 5-6 columns to keep the CSV file readable.'}
              </BodyText>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Les colonnes d\'enrichissement sont-elles disponibles pour toutes les retombées ?' : 'Q: Are enrichment columns available for all coverage?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Oui, les colonnes d\'enrichissement peuvent être ajoutées à n\'importe quelle sélection de retombées. Roland.ia analysera chaque retombée pour remplir ces colonnes.'
                  : 'A: Yes, enrichment columns can be added to any coverage selection. Roland.ia will analyze each coverage to fill these columns.'}
              </BodyText>
            </Box>

            <SectionTitle>{isFrench ? 'Questions sur le contexte et le raisonnement' : 'Questions About Context and Reasoning'}</SectionTitle>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Que faire si Roland.ia semble avoir perdu le fil de la conversation ?' : 'Q: What to do if Roland.ia seems to have lost track of the conversation?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Vous pouvez rafraîchir le contexte en réitérant les informations clés, simplifier votre requête, ou créer une nouvelle conversation si le problème persiste. Consultez la section "Résolution de problèmes" pour plus de détails.'
                  : 'A: You can refresh the context by reiterating key information, simplify your query, or create a new conversation if the problem persists. Consult the "Troubleshooting" section for more details.'}
              </BodyText>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Pourquoi Roland.ia dit parfois qu\'il ne trouve pas de données alors qu\'elles existent ?' : 'Q: Why does Roland.ia sometimes say it can\'t find data when it exists?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Cela peut arriver si le contexte de la conversation est devenu trop long ou confus. Essayez de réitérer votre requête avec des informations clés explicites, ou créez une nouvelle conversation pour repartir avec un contexte propre.'
                  : 'A: This can happen if the conversation context has become too long or confusing. Try reiterating your query with explicit key information, or create a new conversation to start with a clean context.'}
              </BodyText>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : À partir de combien de messages une conversation devient-elle trop longue ?' : 'Q: From how many messages does a conversation become too long?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Généralement, après 20-30 messages, il peut être bénéfique de créer une nouvelle conversation pour éviter la confusion. Cependant, cela dépend de la complexité des requêtes.'
                  : 'A: Generally, after 20-30 messages, it can be beneficial to create a new conversation to avoid confusion. However, this depends on query complexity.'}
              </BodyText>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {isFrench ? 'Q : Puis-je récupérer une conversation où Roland.ia a perdu le fil ?' : 'Q: Can I recover a conversation where Roland.ia has lost track?'}
              </Typography>
              <BodyText>
                {isFrench
                  ? 'R : Oui, vous pouvez essayer de rafraîchir le contexte en réitérant les informations importantes, ou simplifier votre approche en divisant les requêtes complexes en étapes plus simples.'
                  : 'A: Yes, you can try refreshing the context by reiterating important information, or simplify your approach by dividing complex queries into simpler steps.'}
              </BodyText>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Advanced Tips */}
        <Accordion expanded={expandedSection === 'advanced-tips'} onChange={handleChange('advanced-tips')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h5">🎓 {isFrench ? 'Conseils avancés' : 'Advanced Tips'}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SectionTitle>{isFrench ? 'Astuces pour des sélections optimales' : 'Tips for Optimal Selections'}</SectionTitle>
            
            <List>
              {[
                {
                  fr: 'Utilisez des requêtes en plusieurs étapes : Étape 1 "50 retombées pour [Client]", Étape 2 "Parmi celles-là, garde seulement les primaires", Étape 3 "Maintenant équilibre entre positif et négatif"',
                  en: 'Use multi-step queries: Step 1 "50 coverage for [Client]", Step 2 "Among those, keep only the primary ones", Step 3 "Now balance between positive and negative"'
                },
                {
                  fr: 'Demandez des explications : "Pourquoi cette retombée a-t-elle été sélectionnée ?", "Explique-moi les critères appliqués"',
                  en: 'Ask for explanations: "Why was this coverage selected?", "Explain the criteria applied"'
                },
                {
                  fr: 'Comparez des sélections : Créez plusieurs conversations pour comparer différentes approches, utilisez les graphiques pour visualiser les différences',
                  en: 'Compare selections: Create multiple conversations to compare different approaches, use charts to visualize differences'
                },
                {
                  fr: 'Optimisez vos critères : Commencez avec des critères larges, affinez progressivement selon vos besoins, utilisez les justifications pour comprendre les sélections',
                  en: 'Optimize your criteria: Start with broad criteria, refine progressively according to your needs, use justifications to understand selections'
                },
                {
                  fr: 'Utilisez l\'enrichissement stratégiquement : Ajoutez des colonnes qui répondent à vos besoins d\'analyse spécifiques, combinez enrichissement et visualisation pour des insights rapides, réutilisez les colonnes enrichies dans vos requêtes suivantes',
                  en: 'Use enrichment strategically: Add columns that meet your specific analysis needs, combine enrichment and visualization for quick insights, reuse enriched columns in subsequent queries'
                },
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`${index + 1}. ${isFrench ? item.fr : item.en}`}
                    primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                  />
                </ListItem>
              ))}
            </List>

            <CodeBlock>
              {isFrench
                ? `Exemple d'enrichissement stratégique :
✅ "25 retombées avec Catégorie, puis montre-moi seulement celles de la catégorie 'Innovation'"`
                : `Example of strategic enrichment:
✅ "25 coverage with Category, then show me only those in the 'Innovation' category"`}
            </CodeBlock>
          </AccordionDetails>
        </Accordion>

        {/* Support */}
        <Paper 
          sx={{ 
            p: 3, 
            mt: 4, 
            bgcolor: 'background.paper',
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: '1.25rem', color: 'primary.main' }}>
            📞 {isFrench ? 'Support' : 'Support'}
          </Typography>
          <BodyText>
            {isFrench
              ? 'Si vous rencontrez des problèmes ou avez des questions :'
              : 'If you encounter problems or have questions:'}
          </BodyText>
          <List sx={{ mt: 1 }}>
            {[
              { fr: 'Consultez cette documentation', en: 'Consult this documentation' },
              { fr: 'Vérifiez les exemples', en: 'Check examples' },
              { fr: 'Contactez un administrateur', en: 'Contact an administrator' },
            ].map((item, index) => (
              <ListItem key={index} sx={{ pl: 0 }}>
                <InfoIcon color="primary" sx={{ mr: 1.5, flexShrink: 0 }} />
                <ListItemText 
                  primary={`${index + 1}. ${isFrench ? item.fr : item.en}`}
                  primaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            {isFrench ? 'Dernière mise à jour :' : 'Last updated:'} Novembre 2025 | {isFrench ? 'Version :' : 'Version:'} 1.0
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Documentation;
